import type {
  AmhsLiveStreamTuning,
  AmhsStreamCollisionAvoidance,
  AmhsStreamDispatchPolicy,
  AmhsStreamInventory,
  AmhsStreamOhtAssignment,
  AmhsStreamRunRequest,
} from '../../types/amhsStream';
import type { AmhsInventoryConfig, AmhsRunRequest } from '../../types/amhsSimulationRequest';
import { AMHS_RUN_INVENTORY, DEFAULT_DROP_TO_PICK_SEC } from './amhsSimulationConfig';
import {
  buildStreamOhtAssignment,
  resolveSavedOhtAssignment,
  resolveStreamDispatchPolicy,
} from './amhsStreamDispatch';
import { buildAmhsRunRequest, type BuildAmhsRunRequestInput } from './buildAmhsRunRequest';

export const DEFAULT_AMHS_LIVE_STREAM_TUNING: AmhsLiveStreamTuning = {
  /**
   * Sim seconds per wall second.
   *
   * This is the strongest control over how smooth the run looks, because the
   * live clock advances the on-screen timeline by `wallDeltaS * rate * scale`
   * (see `liveClock.ts`) and lets `scale` reach 2 while catching up. The jump
   * a frame paints is therefore proportional to this number: at 10x a frame
   * that stalls to 200ms advanced roughly four sim seconds at once, which on a
   * long interbay chord reads as a vehicle teleporting rather than travelling.
   *
   * Default to a speed that survives a dropped frame. Raise it in the review
   * modal for throughput and KPI runs, where the run is not being watched and
   * covering the horizon quickly matters more than motion fidelity.
   */
  simSpeed: 5,
  continuous: true,
  horizonSec: 3600,
  tickWallSec: 0.1,
  publishEveryNTicks: 5,
  legLookaheadSec: 30,
  maxRetainedLegs: 500,
  maxRetainedTransportRequests: 2000,
  maxRetainedFoups: 200,
  activationsPerTick: 8,
  planningBatchFoups: 20,
  minPendingFoups: 10,
  maxFoupsInSystem: 60,
  inventoryUpdateHoldWallSec: 2,
  pickupHoldWallSec: 15,
  dropHoldWallSec: 15,
  activationLeadWallSec: 0.5,
  collisionHoldRounds: 3,
  maxDeferLookaheads: 2,
  reservationPruneSec: 120,
};

export const DEFAULT_AMHS_STREAM_OHT_ASSIGNMENT: AmhsStreamOhtAssignment =
  buildStreamOhtAssignment('nearest');

export const DEFAULT_AMHS_STREAM_TOOL_PROCESS_WAIT_SEC = 500;

export const DEFAULT_AMHS_STREAM_PIPELINE = {
  persist: false,
  toolProcessWaitSec: DEFAULT_AMHS_STREAM_TOOL_PROCESS_WAIT_SEC,
  includeSimulation: true,
  includeKinetic: false,
  includeTransportFlow: false,
  includeTransportRequests: true,
  includeTransportationGraph: true,
  includeReports: true,
  includeTrOptimization: true,
  includeRouteOptimization: true,
  routeOptimization: {
    enabled: true,
    maxCandidates: 3,
    objective: 'minWait',
    compareBaseline: true,
  },
  includeOhtAssignment: true,
} as const;

export const DEFAULT_AMHS_STREAM_MES = {
  enabled: true,
  releaseGapSec: 4,
  maxFoupsInSystem: 60,
  maxFoups: null,
} as const;

export const DEFAULT_AMHS_STREAM_COLLISION_AVOIDANCE: AmhsStreamCollisionAvoidance = {
  enabled: true,
  mode: 'jointPriority',
  jointForce: true,
  jointReleaseSlackSec: 120,
  headwaySec: 25,
  onConflict: 'wait',
  fallback: 'reroute',
  allowOppositeTraffic: false,
  // Dock capacity (one vehicle per stocker/tool dock) is off by default: with
  // it on, the vehicle resting on a dock takes every job from it and the rest
  // of the fleet sits idle. Junctions are protected regardless: the API
  // serialises vehicles at pass-through nodes unless `enforceJunctionCapacity`
  // is sent as false.
  enforceNodeCapacity: false,
  enforceJunctionCapacity: true,
  defaultNodeCapacity: 1,
  enforceMinSeparation: true,
  absorbWaitIntoPreviousSegment: true,
  nodeClearanceSec: 2,
  minNodeHoldSec: 2,
  returnHomeAfterLastJob: false,
  trackOccupancy: 'movingBlock',
};

export type BuildAmhsStreamRequestInput = BuildAmhsRunRequestInput & {
  simSpeed?: number;
  liveStream?: Partial<AmhsLiveStreamTuning>;
  collisionAvoidance?: Partial<AmhsStreamCollisionAvoidance>;
  dispatchPolicy?: AmhsStreamDispatchPolicy;
};

/** Reads the persisted junction dwell so a saved review value survives remounts. */
export function resolveJunctionWaitSec(
  projectMetadata?: Record<string, unknown> | null
): number | undefined {
  const value = projectMetadata?.junctionWaitSec;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    if (value === 1.5) return 3;
    return value;
  }
  return undefined;
}

/** Reads persisted live stream tuning, migrating legacy default timings (e.g. 2s pickup/drop -> 15s). */
export function resolveSavedLiveStream(
  saved?: Partial<AmhsLiveStreamTuning> | null
): Partial<AmhsLiveStreamTuning> | undefined {
  if (!saved || typeof saved !== 'object') return undefined;
  const resolved = { ...saved };
  if (resolved.pickupHoldWallSec === 2) {
    resolved.pickupHoldWallSec = DEFAULT_AMHS_LIVE_STREAM_TUNING.pickupHoldWallSec; // 15
  }
  if (resolved.dropHoldWallSec === 2) {
    resolved.dropHoldWallSec = DEFAULT_AMHS_LIVE_STREAM_TUNING.dropHoldWallSec; // 15
  }
  return resolved;
}

/** Reads persisted collision-avoidance overrides from the review configuration panel. */
export function resolveSavedCollisionAvoidance(
  projectMetadata?: Record<string, unknown> | null
): Partial<AmhsStreamCollisionAvoidance> | undefined {
  const value = projectMetadata?.collisionAvoidance;
  if (!value || typeof value !== 'object') return undefined;

  const saved = value as Record<string, unknown>;
  const resolved: Partial<AmhsStreamCollisionAvoidance> = {};
  if (
    typeof saved.headwaySec === 'number' &&
    Number.isFinite(saved.headwaySec) &&
    saved.headwaySec >= 0
  ) {
    resolved.headwaySec = saved.headwaySec;
  }
  if (typeof saved.enforceNodeCapacity === 'boolean') {
    resolved.enforceNodeCapacity = saved.enforceNodeCapacity;
  }
  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

export function resolveAmhsFabId(
  floorPlanId?: string | null,
  floorPlanName?: string | null
): string {
  return floorPlanId?.trim() || floorPlanName?.trim() || 'current-layout';
}

/**
 * Occupancy key for the live stream control plane (discover / start / stop).
 * Layout identity stays on `trackNetwork.fabId`; this is only the per-user lock.
 */
export function resolveAmhsStreamOccupancyId(
  userId?: string | null,
  layoutFabId?: string | null
): string {
  return userId?.trim() || layoutFabId?.trim() || 'current-layout';
}

/**
 * Resolve the incoming/outgoing pair the live simulation requires. Falls back to
 * the shared raw/processed defaults so a layout can never open a stream with a
 * column count the API rejects.
 */
function toStreamInventory(inventory: AmhsInventoryConfig | undefined): AmhsStreamInventory {
  const defaults = AMHS_RUN_INVENTORY.stockerColumns ?? [];
  const columns = inventory?.stockerColumns ?? [];
  const raw =
    columns.find((column) => column.kind === 'raw') ??
    defaults.find((column) => column.kind === 'raw');
  const processed =
    columns.find((column) => column.kind === 'processed') ??
    defaults.find((column) => column.kind === 'processed');

  if (!raw || !processed) {
    throw new Error(
      'Live simulation needs one raw and one processed stocker column in inventory.stockerColumns.'
    );
  }

  return {
    enabled: inventory?.enabled ?? true,
    stockerColumns: [raw, processed],
  };
}

function toStreamOhtAssignment(
  assignment: AmhsRunRequest['ohtAssignment'] | undefined,
  projectMetadata?: Record<string, unknown> | null,
  dispatchPolicyOverride?: AmhsStreamDispatchPolicy
): AmhsStreamOhtAssignment {
  const saved = resolveSavedOhtAssignment(projectMetadata);
  const policy =
    dispatchPolicyOverride ??
    resolveStreamDispatchPolicy(
      { ...(assignment as Partial<AmhsStreamOhtAssignment> | undefined), ...saved },
      projectMetadata
    );

  return buildStreamOhtAssignment(policy, {
    preferPreferredOht:
      saved?.preferPreferredOht ?? DEFAULT_AMHS_STREAM_OHT_ASSIGNMENT.preferPreferredOht,
    allowIntrabayEmptyTravel:
      saved?.allowIntrabayEmptyTravel ??
      assignment?.allowIntrabayEmptyTravel ??
      DEFAULT_AMHS_STREAM_OHT_ASSIGNMENT.allowIntrabayEmptyTravel,
    ...(policy === 'ortools'
      ? {
          useOrtools: saved?.useOrtools ?? assignment?.useOrtools ?? true,
          forceOrtools: saved?.forceOrtools ?? assignment?.forceOrtools,
          ortoolsTimeLimitS:
            saved?.ortoolsTimeLimitS ??
            assignment?.ortoolsTimeLimitS ??
            DEFAULT_AMHS_STREAM_OHT_ASSIGNMENT.ortoolsTimeLimitS,
          intelligentSharedOht:
            saved?.intelligentSharedOht ?? assignment?.intelligentSharedOht ?? false,
          sharedOhtMinGainSec:
            saved?.sharedOhtMinGainSec ?? assignment?.sharedOhtMinGainSec ?? 30,
          intelligentDedicatedAssist:
            saved?.intelligentDedicatedAssist ??
            assignment?.intelligentDedicatedAssist ??
            false,
          dedicatedJobsFirst:
            saved?.dedicatedJobsFirst ?? assignment?.dedicatedJobsFirst ?? true,
        }
      : {}),
  });
}

export function buildAmhsStreamRequest(input: BuildAmhsStreamRequestInput): AmhsStreamRunRequest {
  const run = buildAmhsRunRequest(input);
  const fabId = resolveAmhsFabId(input.floorPlanId, input.floorPlanName);

  return {
    loadPorts: run.loadPorts,
    tools: run.tools,
    stockers: run.stockers,
    ohts: run.ohts,
    handling: run.handling ?? { dropToPickSec: DEFAULT_DROP_TO_PICK_SEC },
    trackNetwork: {
      ...run.trackNetwork,
      fabId,
    },
    foupReleaseMode: 'segment-free',
    inventory: toStreamInventory(run.inventory),
    ohtAssignment: toStreamOhtAssignment(
      run.ohtAssignment,
      input.projectMetadata,
      input.dispatchPolicy
    ),
    ...DEFAULT_AMHS_STREAM_PIPELINE,
    includeCollisionAvoidance: true,
    collisionAvoidance: {
      ...DEFAULT_AMHS_STREAM_COLLISION_AVOIDANCE,
      ...resolveSavedCollisionAvoidance(input.projectMetadata),
      ...input.collisionAvoidance,
    },
    mes: { ...DEFAULT_AMHS_STREAM_MES },
    liveStream: {
      ...DEFAULT_AMHS_LIVE_STREAM_TUNING,
      ...resolveSavedLiveStream(input.liveStream),
      simSpeed:
        typeof input.simSpeed === 'number' && input.simSpeed > 0
          ? input.simSpeed
          : DEFAULT_AMHS_LIVE_STREAM_TUNING.simSpeed,
    },
  };
}

function stableJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableJsonValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, stableJsonValue(item)])
  );
}

/** Stable comparison token; it is intentionally not a security hash. */
export function fingerprintAmhsStreamRequest(request: AmhsStreamRunRequest): string {
  const json = JSON.stringify(stableJsonValue(request));
  let hash = 2166136261;
  for (let index = 0; index < json.length; index += 1) {
    hash ^= json.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}
